import { Link } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { OsLink } from "@/components/ui/os-layout/OsLink";
import { useIsOsMode } from "@/components/ui/os-layout";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";

const DEFAULT_LINK_CLASS =
  "p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors";

type OpsSubpageHeadingClassicProps = {
  title: string;
  subtitle?: React.ReactNode;
  /** Shown after back (classic) and after optional subtitle (OS), e.g. status chips */
  after?: React.ReactNode;
  /** Icon or badge between back link and title (classic only); in OS, shown before subtitle/after */
  beforeTitle?: React.ReactNode;
  /** Extra label next to back arrow (classic only), e.g. "Back to Ops" */
  backLabel?: React.ReactNode;
  backTo?: string;
  hubWindowTitle?: string;
  useOsLink?: boolean;
  linkClassName?: string;
  titleClassName?: string;
  /** Classic only: forwarded to the back Link/OsLink for tests */
  backLinkDataTestId?: string;
  /**
   * Classic only: use a button instead of Link/OsLink (e.g. in-window navigation).
   * In OS mode back is still hidden (window chrome / updateWindow handles context).
   */
  onBackClick?: () => void;
  /** Required when using onBackClick for accessibility */
  backButtonAriaLabel?: string;
};

/**
 * Classic Ops sub-page heading: back to /ops + title (+ optional subtitle).
 * In Ani OS, the window title already names the tool — hide back + duplicate title.
 */
export function OpsSubpageHeadingClassic({
  title,
  subtitle,
  after,
  beforeTitle,
  backLabel,
  backTo = "/ops",
  hubWindowTitle = "Operations",
  useOsLink = false,
  linkClassName = DEFAULT_LINK_CLASS,
  titleClassName = "text-xl font-bold text-slate-900 dark:text-white",
  backLinkDataTestId,
  onBackClick,
  backButtonAriaLabel,
}: OpsSubpageHeadingClassicProps) {
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  const inOsShell = isOsMode || Boolean(windowId);

  if (inOsShell) {
    if (subtitle == null && after == null) {
      return null;
    }
    return (
      <div className="flex items-center gap-3 min-w-0 flex-wrap">
        {/* OS window title is enough — skip decorative beforeTitle icons */}
        {subtitle ? (
          <div className="min-w-0 text-sm text-slate-500 dark:text-slate-400">
            {subtitle}
          </div>
        ) : null}
        {after}
      </div>
    );
  }

  const backInner = backLabel ? (
    <span className="flex items-center gap-2">
      <FontAwesomeIcon icon={faArrowLeft} />
      {backLabel}
    </span>
  ) : (
    <FontAwesomeIcon icon={faArrowLeft} />
  );

  const backControl =
    onBackClick != null ? (
      <button
        type="button"
        onClick={onBackClick}
        className={linkClassName}
        aria-label={backButtonAriaLabel}
        data-testid={backLinkDataTestId}
      >
        {backInner}
      </button>
    ) : useOsLink ? (
      <OsLink
        to={backTo}
        className={linkClassName}
        windowTitle={hubWindowTitle}
        data-testid={backLinkDataTestId}
      >
        {backInner}
      </OsLink>
    ) : (
      <Link
        to={backTo}
        className={linkClassName}
        data-testid={backLinkDataTestId}
      >
        {backInner}
      </Link>
    );

  return (
    <div className="flex items-center gap-3 min-w-0 flex-wrap">
      {backControl}
      {beforeTitle}
      <div className="min-w-0">
        <h1 className={titleClassName}>{title}</h1>
        {subtitle}
      </div>
      {after}
    </div>
  );
}
